import { useEffect, useRef, useState } from 'react'
import { Box, Chip, Popover, Tooltip } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import { DimChipSelect } from './DimChipSelect'

interface DimOption {
  value: string
  label: string
}

interface Props {
  dimName: string
  options: DimOption[]
  selected: string[]
  defaultOpen?: boolean
  onChange: (next: string[]) => void
  onRemove: () => void
  /** Row 23: applicability warning — renders a ⚠ + tooltip. */
  warning?: string
  /** FU-32: the chip's value is applied through the active campaign's resolution
   *  (redundant coexistence). Tooltip text; `warning` takes precedence. */
  campaignCovered?: string
}

export function ActiveFilterChip({
  dimName,
  options,
  selected,
  defaultOpen = false,
  onChange,
  onRemove,
  warning,
  campaignCovered,
}: Props) {
  const chipRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  // Mount-only auto-open when defaultOpen=true. Anchor to the chip's own DOM node
  // because MUI Popover requires a real anchorEl — an unanchored Popover renders
  // detached at top-left of the viewport. Subsequent re-renders do not re-trigger
  // (the user may have already closed it).
  useEffect(() => {
    if (defaultOpen && chipRef.current) {
      setAnchorEl(chipRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const labelMap = Object.fromEntries(options.map((o) => [o.value, o.label]))

  // Row 26: per-value deselect. Deleting the LAST value removes the whole dim filter
  // (deliberate contrast to the M5 popover ghost re-arm, which only applies mid-popover-edit).
  const handleValueDelete = (value: string) => {
    const next = selected.filter((v) => v !== value)
    if (next.length === 0) onRemove()
    else onChange(next)
  }

  const handleClose = () => {
    setAnchorEl(null)
    if (selected.length === 0) {
      onRemove()
    }
  }

  const chip = (
    <Chip
      ref={chipRef}
      label={dimName}
      icon={
        warning ? (
          <WarningAmberIcon color="warning" />
        ) : campaignCovered ? (
          <CampaignOutlinedIcon
            data-testid="campaign-covered-badge"
            sx={{ color: 'text.disabled' }}
          />
        ) : undefined
      }
      onClick={(e) => setAnchorEl(e.currentTarget)}
      onDelete={onRemove}
      color="primary"
      variant="outlined"
      clickable
      data-testid="dim-filter-chip"
    />
  )

  const tooltipTitle = warning ?? campaignCovered

  return (
    <>
      {tooltipTitle ? <Tooltip title={tooltipTitle}>{chip}</Tooltip> : chip}
      {selected.map((v) => (
        <Chip
          key={v}
          size="small"
          label={labelMap[v] ?? v}
          onDelete={() => handleValueDelete(v)}
          data-testid="dim-filter-value-chip"
        />
      ))}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <DimChipSelect
            dimName={dimName}
            options={options}
            selected={selected}
            onChange={onChange}
          />
        </Box>
      </Popover>
    </>
  )
}
